import { ArrowLeftIcon, Button, EmptyState, RoomIcon, SendIcon } from '@bp/ui-components';
import { Capacitor } from '@capacitor/core';
import classNames from 'classnames';
import { useTypingCommands } from 'matrix/commands/useTypingCommands';
import type { MatrixEvent, Room } from 'matrix-js-sdk';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsDesktop } from '../../../hooks/useIsDesktop';
import { useMessageCommands } from '../../../matrix/commands/useMessageCommands';
import { useConversations } from '../../../matrix/hooks/useConversations';
import { useTypingUsers } from '../../../matrix/hooks/useTypingUsers';
import styles from './MatrixConversation.module.scss';
import { MatrixConversationItem } from './MatrixConversationItem/MatrixConversationItem';

type ConversationProps = {
  roomId: string;
  onBackClick: () => void;
};

export const MatrixConversation = ({ roomId, onBackClick }: ConversationProps) => {
  const { t } = useTranslation();
  const conversation = useConversations(roomId);
  const { setTyping } = useTypingCommands();
  const { sendMessage, markAsRead } = useMessageCommands();
  const typingUsers = useTypingUsers(roomId);
  const isDesktop = useIsDesktop();

  const chatRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  const [input, setInput] = useState<string>('');
  const [inputRows, setInputRows] = useState<number>(1);
  const [preventScroll, setPreventScroll] = useState<boolean>(false);

  const isInputEmpty = (input: string) => !input || input === '' || !input.trim().length;

  function handleInputHeight() {
    const rows = Math.floor((inputRef.current?.scrollHeight ?? 18) / 18);
    setInputRows(rows >= 3 ? 3 : rows);
  }

  async function handleSend() {
    void setTyping(roomId, false);
    if (isInputEmpty(input)) return;
    await sendMessage(roomId, input.trim());
    setPreventScroll(false);
    setInput('');
    setInputRows(1);
    setPreventScroll(false);
  }

  // smooth scroll after submit and for incoming messages
  // biome-ignore lint/correctness/useExhaustiveDependencies: scroll on room change
  useEffect(() => {
    if (chatRef.current && !preventScroll) {
      chatRef.current.scrollTo({
        top: chatRef.current.scrollHeight,
        behavior: 'auto',
      });
    }
  }, [conversation, preventScroll]);

  // jump to end on room selection
  // biome-ignore lint/correctness/useExhaustiveDependencies: scroll on room change
  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTo({
        top: chatRef.current.scrollHeight,
        behavior: 'smooth',
      });
      setPreventScroll(false);
    }
  }, [preventScroll]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: scroll on room change
  useEffect(() => {
    void markAsRead(roomId);
  }, [roomId, markAsRead, conversation]);

  return (
    <div className={styles.conversation}>
      <div className={styles.room}>
        {!isDesktop && (
          <Button
            icon={<ArrowLeftIcon />}
            hierarchy='ghost'
            onClick={() => {
              onBackClick();
            }}
          />
        )}
        {t('messages.chatWith', { name: conversation.roomName })}
      </div>
      <div className={styles.chat} ref={chatRef}>
        {conversation.chunks.map((chunk) => {
          return <MatrixConversationItem chunk={chunk} key={chunk.id} />;
        })}
      </div>
      {!isDesktop && typingUsers.length > 0 && (
        <div className={styles['typing-indicator']}>
          {t('messages.isTyping', { count: typingUsers.length, name: typingUsers[0].name })}
        </div>
      )}
      <div className={styles['input-area']}>
        <textarea
          ref={inputRef}
          name='input'
          className={classNames(styles.input, 'has-scrollbar')}
          rows={inputRows}
          placeholder={t('messages.typeMessage')}
          onBlur={() => {
            void setTyping(roomId, false);
          }}
          onInput={(e) => {
            if (isInputEmpty(e.currentTarget.value)) {
              setInputRows(1);
              setInput('');
              return;
            }
            setInput(e.currentTarget.value);
            handleInputHeight();
          }}
          onKeyDown={async (e) => {
            if (
              e.key === 'Enter' &&
              !(Capacitor.getPlatform() === 'ios' || Capacitor.getPlatform() === 'android') &&
              !e.shiftKey
            ) {
              e.stopPropagation();
              e.preventDefault();
              handleSend();
            } else if (e.key === 'Escape') {
              await setTyping(roomId, false);
              inputRef.current?.blur();
            } else {
              await setTyping(roomId, true);
            }
          }}
          value={input}
        />
        <Button className={styles['send-button']} icon={<SendIcon />} onClick={async () => handleSend()} />
      </div>
    </div>
  );
};
